import { addDays, endOfDay, startOfDay } from "date-fns";
import { chargeInstances, type ChargeInstance, type ScheduledCharge } from "./chargeSchedule";

/** Lecture des échéances pour l'utilisateur : ce qui va être prélevé, ce qui
 *  vient de l'être et attend sa confirmation, et les abonnements de comptes
 *  cramés dont on ne sait pas s'ils ont été résiliés. Pur, partagé Lite /
 *  Advanced. */

/** Jours pendant lesquels une échéance passée reste « à confirmer ». Sans
 *  réponse, elle est comptée comme payée (elle l'est dès sa date). */
export const CONFIRM_WINDOW_DAYS = 7;

const SUBSCRIPTION_SOURCE = "prop_firm_subscription";

export interface ReviewCharge extends ScheduledCharge {
  id: string;
  name: string;
  accountId?: string | null;
  source?: string | null;
  cancelAskedAt?: Date | string | null;
  renewalConfirmedAt?: Date | string | null;
}

export interface ReviewRow<C extends ReviewCharge> {
  charge: C;
  instance: ChargeInstance;
}

const recurring = (c: ReviewCharge) => c.frequency !== "one_time";

function rows<C extends ReviewCharge>(charges: C[], from: Date, to: Date, includeSkipped: boolean): ReviewRow<C>[] {
  return charges
    .filter(recurring)
    .flatMap(charge => chargeInstances(charge, from, to, { includeSkipped }).map(instance => ({ charge, instance })))
    .sort((a, b) => a.instance.date.getTime() - b.instance.date.getTime());
}

/** Échéances des `days` prochains jours, aujourd'hui compris. Les sautées sont
 *  gardées (montant 0) pour que l'utilisateur puisse les rétablir. */
export function upcomingInstances<C extends ReviewCharge>(charges: C[], days = 30, now = new Date()): ReviewRow<C>[] {
  return rows(charges, startOfDay(now), endOfDay(addDays(now, days)), true);
}

/** Échéances passées depuis moins de CONFIRM_WINDOW_DAYS jours, hier compris,
 *  sans aucune réponse de l'utilisateur. */
export function pendingConfirmations<C extends ReviewCharge>(charges: C[], now = new Date()): ReviewRow<C>[] {
  return rows(charges, startOfDay(addDays(now, -CONFIRM_WINDOW_DAYS)), endOfDay(addDays(now, -1)), false)
    .filter(r => r.instance.status === null);
}

/** Abonnements prop firm encore ouverts dont le compte a été cramé et qui
 *  attendent la réponse « as-tu résilié ? ». */
export function cancelQuestions<C extends ReviewCharge>(charges: C[]): C[] {
  return charges.filter(c =>
    c.source === SUBSCRIPTION_SOURCE && !!c.cancelAskedAt && !c.renewalConfirmedAt && !c.endDate,
  );
}
